// utils/nationalMembers.js
//
// Every current member of Congress, searchable by name, state or district, for
// the Representatives page's nationwide results.
//
// The members endpoint (api.govglance.org/members/) pages by limit/skip and has
// no search of its own -- useMembersPageData filters whatever page it happens
// to have fetched, which is why the House and Senate list pages can only find
// people already scrolled past. Searching everyone means holding everyone, so
// the whole list is read once, the first time somebody searches, and kept for
// the session as a shared promise: every later search is instant and makes no
// request.
//
// Former members are left out. A member's latest term carries an end year once
// it is over; a term with none is the one being served now. If the API stops
// sending endYear at all, the filter lets everyone through rather than
// dropping everyone.

import { extractMemberData, MEMBER_TYPES } from './membersConfig';

const MEMBERS_URL = 'https://api.govglance.org/members/';
const apiKey = process.env.REACT_APP_API_KEY;

const PAGE_SIZE = 500;
// A ceiling, not an expectation: it stops a misbehaving endpoint that always
// returns full pages from looping forever.
const MAX_PAGES = 40;

let cache = null;

const isCurrent = (raw) => {
  const terms = Array.isArray(raw?.terms) ? raw.terms : [];
  const latest = terms[terms.length - 1];
  if (!latest) return false;
  return latest.endYear === undefined || latest.endYear === null || latest.endYear === '';
};

const latestParty = (raw) => {
  const history = Array.isArray(raw?.party_history) ? raw.party_history : [];
  const latest = history[history.length - 1];
  return latest?.partyName || latest?.party_name || null;
};

// The shape RepresentativesPage renders and opens, so a nationwide match is the
// same card as one of the reader's own officials.
const toOfficial = (raw) => {
  const member = extractMemberData(raw, MEMBER_TYPES.HOUSE);
  if (!member?.bioGuideId) return null;

  const senate = member.chamber === 'Senate';
  const address = member.addressInfo || null;

  return {
    key: `nationwide-${member.bioGuideId}`,
    bioGuideId: member.bioGuideId,
    name: member.fullName,
    level: 'federal',
    chamber: senate ? 'senate' : 'house',
    stateName: member.stateName || null,
    district: member.district != null ? String(member.district) : '',
    party: latestParty(raw),
    photoUrl: member.imageUrl || null,
    website: member.websiteUrl || null,
    // MemberDetailPage normalizes the old address_information object shape.
    officeAddress: address,
    phone: address?.phoneNumber || address?.phone || null,
    termStartYear: member.startYear || null,
    actions: [],
  };
};

const fetchPage = async (skip) => {
  const response = await fetch(`${MEMBERS_URL}?limit=${PAGE_SIZE}&skip=${skip}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
  });
  if (!response.ok) throw new Error(`members ${response.status}`);
  const body = await response.json();
  return Array.isArray(body) ? body : [];
};

/**
 * Every current member of Congress, read once per session. Resolves to an
 * array of official-shaped rows. A failure is not cached, so the next search
 * tries again.
 */
export const loadAllMembers = () => {
  if (cache) return cache;

  cache = (async () => {
    const seen = new Set();
    const rows = [];

    for (let page = 0; page < MAX_PAGES; page += 1) {
      const batch = await fetchPage(page * PAGE_SIZE);
      batch.forEach((raw) => {
        if (!isCurrent(raw)) return;
        const official = toOfficial(raw);
        if (!official || seen.has(official.bioGuideId)) return;
        seen.add(official.bioGuideId);
        rows.push(official);
      });
      if (batch.length < PAGE_SIZE) break;
    }

    return rows.sort((a, b) => a.name.localeCompare(b.name));
  })().catch((error) => {
    cache = null;
    throw error;
  });

  return cache;
};

const STATE_ABBR = {
  alabama: 'al', alaska: 'ak', arizona: 'az', arkansas: 'ar', california: 'ca', colorado: 'co',
  connecticut: 'ct', delaware: 'de', florida: 'fl', georgia: 'ga', hawaii: 'hi', idaho: 'id',
  illinois: 'il', indiana: 'in', iowa: 'ia', kansas: 'ks', kentucky: 'ky', louisiana: 'la',
  maine: 'me', maryland: 'md', massachusetts: 'ma', michigan: 'mi', minnesota: 'mn',
  mississippi: 'ms', missouri: 'mo', montana: 'mt', nebraska: 'ne', nevada: 'nv',
  'new hampshire': 'nh', 'new jersey': 'nj', 'new mexico': 'nm', 'new york': 'ny',
  'north carolina': 'nc', 'north dakota': 'nd', ohio: 'oh', oklahoma: 'ok', oregon: 'or',
  pennsylvania: 'pa', 'rhode island': 'ri', 'south carolina': 'sc', 'south dakota': 'sd',
  tennessee: 'tn', texas: 'tx', utah: 'ut', vermont: 'vt', virginia: 'va', washington: 'wa',
  'west virginia': 'wv', wisconsin: 'wi', wyoming: 'wy',
};

/**
 * Members matching a query. Every word has to match somewhere -- the name, the
 * state's name or two-letter code, or the district -- so "graham" finds Lindsey
 * Graham, "indiana" finds Indiana's delegation, and "tx 7" finds Texas's 7th.
 */
export const searchMembers = (members, query) => {
  const words = String(query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return [];

  return members.filter((member) => {
    const state = (member.stateName || '').toLowerCase();
    const haystack = [
      member.name.toLowerCase(),
      state,
      STATE_ABBR[state] || '',
      member.district ? `district ${member.district} ${member.district}` : '',
      member.chamber === 'senate' ? 'senator senate' : 'representative house',
      (member.party || '').toLowerCase(),
    ].join(' ');
    return words.every((word) => haystack.includes(word));
  });
};